import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useApp } from '../App';
import { get, postAuth } from '../data/api';
import {
  JAPAN_REGIONS,
  POPULAR_CITIES,
  DEFAULT_PREFECTURE,
  findRegionForPrefecture,
} from '../data/japan_locations';
import './Community.css';
import {
  BusinessSubmitModal,
  EventSubmitModal,
  JobSubmitModal,
  AlbumSubmitModal,
  FeaturedRequestModal,
} from './CommunitySubmit';

const TABS = [
  { id: 'members',    label: '👥 Members',    icon: '👥' },
  { id: 'businesses', label: '🏪 Businesses', icon: '🏪' },
  { id: 'events',     label: '📅 Events',     icon: '📅' },
  { id: 'jobs',       label: '💼 Jobs',       icon: '💼' },
  { id: 'albums',     label: '📷 Albums',     icon: '📷' },
];

function ProfileModal({ prefecture, city, onClose, onSaved }) {
  const { user } = useApp();
  const [form, setForm] = useState({
    japan_prefecture: prefecture,
    japan_city:       city || '',
    kenya_county:     '',
    bio:              '',
    skills:           '',
    services_offered: '',
    whatsapp:         '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError]       = useState('');

  async function save(e) {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const data = await postAuth('members', { action: 'update' }, form);
      if (!data.ok) { setError(data.error || 'Update failed'); return; }
      onSaved(form);
      onClose();
    } catch {
      setError('Network error — try again.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="comm-modal-backdrop" onClick={onClose}>
      <div className="comm-modal" onClick={e => e.stopPropagation()}>
        <h2>Complete your profile</h2>
        <p className="comm-modal-sub">
          Help other Kenyans in Japan find you, {user?.full_name || user?.name || 'friend'}.
        </p>
        <form onSubmit={save}>
          <div className="comm-field">
            <label>Prefecture</label>
            <select
              value={form.japan_prefecture}
              onChange={e => setForm(f => ({ ...f, japan_prefecture: e.target.value, japan_city: '' }))}
            >
              {JAPAN_REGIONS.flatMap(r => r.prefectures).map(p => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
          </div>
          <div className="comm-field">
            <label>City / Area</label>
            <input
              list="city-suggestions"
              value={form.japan_city}
              onChange={e => setForm(f => ({ ...f, japan_city: e.target.value }))}
              placeholder="e.g. Shinjuku"
            />
            <datalist id="city-suggestions">
              {(POPULAR_CITIES[form.japan_prefecture] || []).map(c => (
                <option key={c} value={c} />
              ))}
            </datalist>
          </div>
          <div className="comm-field">
            <label>Kenya county (optional)</label>
            <input
              value={form.kenya_county}
              onChange={e => setForm(f => ({ ...f, kenya_county: e.target.value }))}
              placeholder="e.g. Kericho"
            />
          </div>
          <div className="comm-field">
            <label>Short bio</label>
            <textarea
              value={form.bio}
              onChange={e => setForm(f => ({ ...f, bio: e.target.value }))}
              placeholder="A sentence about you..."
            />
          </div>
          <div className="comm-field">
            <label>Skills (comma-separated)</label>
            <input
              value={form.skills}
              onChange={e => setForm(f => ({ ...f, skills: e.target.value }))}
              placeholder="Tech, Swahili teaching, Tax help"
            />
          </div>
          <div className="comm-field">
            <label>Services offered (comma-separated)</label>
            <input
              value={form.services_offered}
              onChange={e => setForm(f => ({ ...f, services_offered: e.target.value }))}
              placeholder="Visa advice, Housing search"
            />
          </div>
          <div className="comm-field">
            <label>WhatsApp</label>
            <input
              value={form.whatsapp}
              onChange={e => setForm(f => ({ ...f, whatsapp: e.target.value }))}
              placeholder="+81 90..."
            />
          </div>
          {error && <p className="text-red text-sm">{error}</p>}
          <div className="comm-modal-actions">
            <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Saving…' : 'Save profile'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function MemberCard({ m }) {
  const initial = (m.name || '?')[0].toUpperCase();
  return (
    <div className="comm-card">
      <div className="comm-card-head">
        <div className="comm-avatar">{initial}</div>
        <div>
          <div className="comm-card-title">{m.name}</div>
          <div className="comm-card-meta">
            📍 {[m.japan_city, m.japan_prefecture].filter(Boolean).join(', ')}
            {m.kenya_county && ` · 🇰🇪 ${m.kenya_county}`}
          </div>
        </div>
      </div>
      {m.bio && <p className="comm-card-body">{m.bio}</p>}
      <div className="comm-badges">
        {m.is_verified === '1' || m.is_verified === 1 ? (
          <span className="comm-badge verified">✓ Verified</span>
        ) : null}
        {m.membership_tier && m.membership_tier !== 'free' && (
          <span className="comm-badge featured">{m.membership_tier}</span>
        )}
      </div>
      {(m.skills_array?.length > 0 || m.services_array?.length > 0) && (
        <div className="comm-tags" style={{ marginTop: 10 }}>
          {[...(m.skills_array || []), ...(m.services_array || [])].slice(0, 6).map(t => (
            <span key={t} className="comm-tag">{t}</span>
          ))}
        </div>
      )}
      {m.whatsapp && (
        <a href={`https://wa.me/${m.whatsapp.replace(/\D/g, '')}`} className="btn btn-outline btn-sm" style={{ marginTop: 12 }}>
          WhatsApp
        </a>
      )}
    </div>
  );
}

function BusinessCard({ b, onFeature, canFeature }) {
  const featured = b.listing_tier === 'featured';
  return (
    <div className={`comm-card ${featured ? 'featured' : ''}`}>
      <div className="comm-card-title">{b.name}</div>
      <div className="comm-card-meta">{b.category} · 📍 {[b.japan_city, b.japan_prefecture].filter(Boolean).join(', ')}</div>
      <p className="comm-card-body">{b.description?.slice(0, 120)}{b.description?.length > 120 ? '…' : ''}</p>
      <div className="comm-badges">
        {featured && <span className="comm-badge featured">Featured</span>}
        {b.phone && <span className="comm-badge">{b.phone}</span>}
      </div>
      {canFeature && onFeature && (
        <button type="button" className="btn btn-ghost btn-sm" style={{ marginTop: 8 }} onClick={() => onFeature(b, 'business')}>
          ⭐ Feature listing
        </button>
      )}
    </div>
  );
}

function EventCard({ ev }) {
  const dateStr = ev.event_date
    ? new Date(ev.event_date + 'T00:00:00').toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })
    : '';
  return (
    <div className="comm-card">
      <div className="comm-card-title">{ev.title}</div>
      <div className="comm-card-meta">
        📅 {dateStr}{ev.event_time ? ` · ${ev.event_time.slice(0, 5)}` : ''}
        {ev.venue && ` · ${ev.venue}`}
      </div>
      <div className="comm-card-meta">📍 {[ev.japan_city, ev.japan_prefecture].filter(Boolean).join(', ')}</div>
      <p className="comm-card-body">{ev.description?.slice(0, 100)}{ev.description?.length > 100 ? '…' : ''}</p>
      <div className="comm-badges">
        {ev.is_free === '1' || ev.is_free === 1 ? (
          <span className="comm-badge free">Free</span>
        ) : (
          <span className="comm-badge">¥{ev.ticket_price}</span>
        )}
        {ev.rsvp_count > 0 && <span className="comm-badge">{ev.rsvp_count} RSVPs</span>}
        <span className="comm-badge">by {ev.organiser_name}</span>
      </div>
    </div>
  );
}

function JobCard({ j, onFeature, canFeature }) {
  return (
    <div className={`comm-card ${j.listing_tier === 'featured' ? 'featured' : ''}`}>
      <div className="comm-card-title">{j.title}</div>
      <div className="comm-card-meta">{j.company} · {j.type === 'baito' ? 'Part-time' : 'Full-time'}</div>
      <div className="comm-card-meta">📍 {[j.japan_city, j.japan_prefecture].filter(Boolean).join(', ')}</div>
      {j.salary && <div className="comm-card-meta">💴 {j.salary}</div>}
      <p className="comm-card-body">{j.description?.slice(0, 100)}{j.description?.length > 100 ? '…' : ''}</p>
      <div className="comm-badges">
        {j.visa_support === '1' || j.visa_support === 1 ? <span className="comm-badge verified">Visa support</span> : null}
        {j.english_ok === '1' || j.english_ok === 1 ? <span className="comm-badge">English OK</span> : null}
        {(j.tags_array || []).slice(0, 3).map(t => <span key={t} className="comm-tag">{t}</span>)}
      </div>
      {(j.apply_url || j.apply_email) && (
        <a href={j.apply_url || `mailto:${j.apply_email}`} className="btn btn-primary btn-sm" style={{ marginTop: 12 }}>
          Apply
        </a>
      )}
      {canFeature && onFeature && (
        <button type="button" className="btn btn-ghost btn-sm" style={{ marginTop: 8 }} onClick={() => onFeature(j, 'job')}>
          ⭐ Feature listing
        </button>
      )}
    </div>
  );
}

function AlbumCard({ a }) {
  return (
    <div className="comm-card">
      {a.cover_url && (
        <img src={a.cover_url} alt="" style={{ width: '100%', height: 120, objectFit: 'cover', borderRadius: 8, marginBottom: 10 }} />
      )}
      <div className="comm-card-title">{a.title}</div>
      <div className="comm-card-meta">📍 {[a.japan_city, a.japan_prefecture].filter(Boolean).join(', ')}</div>
      <div className="comm-badges" style={{ marginTop: 8 }}>
        <span className="comm-badge">{a.photo_count || 0} photos</span>
        <span className="comm-badge">by {a.creator_name}</span>
      </div>
    </div>
  );
}

export default function Community() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { token, user } = useApp();

  const [prefecture, setPrefecture] = useState(params.get('prefecture') || DEFAULT_PREFECTURE);
  const [city, setCity]             = useState(params.get('city') || '');
  const [openRegion, setOpenRegion] = useState(findRegionForPrefecture(prefecture).id);
  const [tab, setTab]               = useState(params.get('tab') || 'members');
  const [search, setSearch]         = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');

  const [items, setItems]     = useState([]);
  const [total, setTotal]     = useState(0);
  const [stats, setStats]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [showProfile, setShowProfile] = useState(false);
  const [profileDone, setProfileDone] = useState(false);
  const [submitType, setSubmitType] = useState(null);
  const [featureTarget, setFeatureTarget] = useState(null);

  const uid = user?.user_id || user?.id;
  const isOwner = (item, field) => token && String(item[field]) === String(uid);

  function openFeature(item, entityType) {
    const label = item.name || item.title;
    setFeatureTarget({ entityType, entityId: item.id, entityLabel: label });
  }

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setParams({ prefecture, ...(city ? { city } : {}), tab }, { replace: true });
  }, [prefecture, city, tab, setParams]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const base = { prefecture, ...(city ? { city } : {}), ...(debouncedQ ? { q: debouncedQ } : {}) };

      if (tab === 'members') {
        const [list, st] = await Promise.all([
          get('members', { action: 'list', ...base }),
          get('members', { action: 'stats', prefecture }),
        ]);
        if (!list.ok) throw new Error(list.error || list.detail);
        setItems(list.members || []);
        setTotal(list.total || 0);
        setStats(st);
      } else if (tab === 'businesses') {
        const data = await get('businesses', { action: 'list', ...base });
        if (!data.ok) throw new Error(data.error);
        setItems(data.businesses || []);
        setTotal(data.total || 0);
        setStats(null);
      } else if (tab === 'events') {
        const data = await get('events', { action: 'list', ...base });
        if (!data.ok) throw new Error(data.error);
        setItems(data.events || []);
        setTotal(data.events?.length || 0);
        setStats(null);
      } else if (tab === 'jobs') {
        const data = await get('jobs', { action: 'list', ...base });
        if (!data.ok) throw new Error(data.error);
        setItems(data.jobs || []);
        setTotal(data.total || 0);
        setStats(null);
      } else if (tab === 'albums') {
        const data = await get('albums', { action: 'list', ...base });
        if (!data.ok) throw new Error(data.error);
        setItems(data.albums || []);
        setTotal(data.albums?.length || 0);
        setStats(null);
      }
    } catch (err) {
      setError(err.message || 'Failed to load');
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [prefecture, city, tab, debouncedQ]);

  useEffect(() => { fetchData(); }, [fetchData]);

  function selectPrefecture(pref) {
    setPrefecture(pref);
    setCity('');
    setOpenRegion(findRegionForPrefecture(pref).id);
  }

  const cities = POPULAR_CITIES[prefecture] || [];
  const needsProfile = token && !profileDone && !user?.japan_prefecture;

  return (
    <div className="comm-page">
      <nav className="comm-nav">
        <div className="container comm-nav-inner">
          <Link to="/" className="comm-logo">mudiaspora<span style={{ color: 'var(--text-muted)' }}>.</span></Link>
          <div className="comm-nav-links">
            <Link to="/">Kenya Services</Link>
            <Link to="/community">Japan Community</Link>
            <Link to="/japan-life">Japan Life</Link>
            <Link to="/kenya-connect">Kenya Connect</Link>
            {token ? (
              <>
                <Link to="/dashboard">Dashboard</Link>
                <button type="button" onClick={() => setShowProfile(true)}>My Profile</button>
              </>
            ) : (
              <>
                <Link to="/login">Sign In</Link>
                <button type="button" className="btn btn-primary btn-sm" onClick={() => navigate('/signup')}>
                  Join free
                </button>
              </>
            )}
          </div>
        </div>
      </nav>

      <section className="comm-hero">
        <div className="container">
          <h1>Kenyan community in <span>Japan</span></h1>
          <p>Find Kenyans near you, discover businesses, events, and jobs — by prefecture and city.</p>
        </div>
      </section>

      <div className="container comm-layout">
        <aside className="comm-sidebar">
          <div className="comm-sidebar-title">Region → Prefecture</div>
          {JAPAN_REGIONS.map(region => (
            <div key={region.id} className="comm-region">
              <button
                type="button"
                className={`comm-region-btn ${openRegion === region.id ? 'open' : ''}`}
                onClick={() => setOpenRegion(openRegion === region.id ? null : region.id)}
              >
                {region.name}
                <span>{openRegion === region.id ? '▾' : '▸'}</span>
              </button>
              {openRegion === region.id && (
                <div className="comm-pref-list">
                  {region.prefectures.map(pref => (
                    <button
                      key={pref}
                      type="button"
                      className={`comm-pref-btn ${prefecture === pref ? 'active' : ''}`}
                      onClick={() => selectPrefecture(pref)}
                    >
                      {pref}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </aside>

        <main className="comm-main">
          {needsProfile && (
            <div className="comm-banner">
              <p>🇯🇵 <strong>Join the directory</strong> — add your prefecture and city so others can find you.</p>
              <button type="button" className="btn btn-amber btn-sm" onClick={() => setShowProfile(true)}>
                Complete profile
              </button>
            </div>
          )}

          <div className="comm-toolbar">
            <input
              className="comm-search"
              placeholder={`Search ${tab} in ${prefecture}…`}
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            {cities.length > 0 && (
              <select
                className="comm-city-select"
                value={city}
                onChange={e => setCity(e.target.value)}
              >
                <option value="">All cities</option>
                {cities.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            )}
          </div>

          {tab === 'members' && stats && (
            <div className="comm-stats">
              <div className="comm-stat">
                <div className="comm-stat-num">{stats.members ?? 0}</div>
                <div className="comm-stat-label">Members</div>
              </div>
              <div className="comm-stat">
                <div className="comm-stat-num">{stats.verified ?? 0}</div>
                <div className="comm-stat-label">Verified</div>
              </div>
              <div className="comm-stat">
                <div className="comm-stat-num">{stats.counties ?? 0}</div>
                <div className="comm-stat-label">Kenya counties</div>
              </div>
            </div>
          )}

          <div className="comm-tabs">
            {TABS.map(t => (
              <button
                key={t.id}
                type="button"
                className={`comm-tab ${tab === t.id ? 'active' : ''}`}
                onClick={() => setTab(t.id)}
              >
                {t.label}
              </button>
            ))}
          </div>

          {token && tab !== 'members' && (
            <div className="comm-list-actions">
              {tab === 'businesses' && (
                <button type="button" className="btn btn-primary btn-sm" onClick={() => setSubmitType('business')}>
                  + List your business
                </button>
              )}
              {tab === 'events' && (
                <button type="button" className="btn btn-primary btn-sm" onClick={() => setSubmitType('event')}>
                  + Post an event
                </button>
              )}
              {tab === 'jobs' && (
                <button type="button" className="btn btn-primary btn-sm" onClick={() => setSubmitType('job')}>
                  + Post a job
                </button>
              )}
              {tab === 'albums' && (
                <button type="button" className="btn btn-primary btn-sm" onClick={() => setSubmitType('album')}>
                  + Share photos
                </button>
              )}
            </div>
          )}

          {!token && tab !== 'members' && (
            <p className="text-sm text-muted" style={{ marginBottom: 16 }}>
              <Link to="/login">Sign in</Link> to list a {tab.slice(0, -1)}.
            </p>
          )}

          {loading && <div className="comm-loading">Loading…</div>}

          {!loading && error && (
            <div className="comm-empty">
              <div className="comm-empty-icon">⚠️</div>
              <p>{error}</p>
              <button type="button" className="btn btn-outline btn-sm" onClick={fetchData} style={{ marginTop: 12 }}>
                Retry
              </button>
            </div>
          )}

          {!loading && !error && items.length === 0 && (
            <div className="comm-empty">
              <div className="comm-empty-icon">
                {tab === 'members' ? '👥' : tab === 'businesses' ? '🏪' : tab === 'events' ? '📅' : tab === 'albums' ? '📷' : '💼'}
              </div>
              <p><strong>No {tab} yet in {prefecture}{city ? `, ${city}` : ''}.</strong></p>
              <p className="text-sm text-muted" style={{ marginTop: 8 }}>
                {token ? 'Be the first — complete your profile above.' : 'Sign up and add your profile to appear here.'}
              </p>
              {!token && (
                <button type="button" className="btn btn-primary btn-sm" style={{ marginTop: 16 }} onClick={() => navigate('/signup')}>
                  Join mudiaspora
                </button>
              )}
            </div>
          )}

          {!loading && !error && items.length > 0 && (
            <>
              <p className="text-sm text-muted" style={{ marginBottom: 12 }}>
                {total} result{total !== 1 ? 's' : ''} in {prefecture}{city ? ` · ${city}` : ''}
              </p>
              <div className="comm-grid">
                {tab === 'members'    && items.map(m => <MemberCard key={m.id} m={m} />)}
                {tab === 'businesses' && items.map(b => (
                  <BusinessCard key={b.id} b={b} canFeature={isOwner(b, 'owner_id')} onFeature={openFeature} />
                ))}
                {tab === 'events'     && items.map(ev => <EventCard key={ev.id} ev={ev} />)}
                {tab === 'jobs'       && items.map(j => (
                  <JobCard key={j.id} j={j} canFeature={isOwner(j, 'posted_by')} onFeature={openFeature} />
                ))}
                {tab === 'albums'     && items.map(a => <AlbumCard key={a.id} a={a} />)}
              </div>
            </>
          )}
        </main>
      </div>

      {showProfile && token && (
        <ProfileModal
          prefecture={prefecture}
          city={city}
          onClose={() => setShowProfile(false)}
          onSaved={() => { setProfileDone(true); fetchData(); }}
        />
      )}

      {submitType === 'business' && (
        <BusinessSubmitModal prefecture={prefecture} city={city}
          onClose={() => setSubmitType(null)} onDone={fetchData} />
      )}
      {submitType === 'event' && (
        <EventSubmitModal prefecture={prefecture} city={city}
          onClose={() => setSubmitType(null)} onDone={fetchData} />
      )}
      {submitType === 'job' && (
        <JobSubmitModal prefecture={prefecture} city={city}
          onClose={() => setSubmitType(null)} onDone={fetchData} />
      )}
      {submitType === 'album' && (
        <AlbumSubmitModal prefecture={prefecture} city={city}
          onClose={() => setSubmitType(null)} onDone={fetchData} />
      )}
      {featureTarget && (
        <FeaturedRequestModal
          entityType={featureTarget.entityType}
          entityId={featureTarget.entityId}
          entityLabel={featureTarget.entityLabel}
          onClose={() => setFeatureTarget(null)}
        />
      )}
    </div>
  );
}
